import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import accountVerificationService from '../../../services/accountVerificationService';

const FailedConfirmationsTable = ({ onActionComplete, refreshTrigger }) => {
  const [failedConfirmations, setFailedConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkSelection, setBulkSelection] = useState([]);

  useEffect(() => {
    loadFailedConfirmations();
  }, [refreshTrigger]);

  const loadFailedConfirmations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountVerificationService?.getFailedConfirmations();
      setFailedConfirmations(data);
    } catch (err) {
      setError(err?.message || 'Failed to load failed confirmations');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (userId, email) => {
    if (!confirm(`Resend verification email to ${email}?`)) return;

    try {
      setProcessingId(userId);
      setError('');
      await accountVerificationService?.resendVerificationEmail(userId);
      alert('Verification email sent successfully!');
      await loadFailedConfirmations();
      onActionComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to resend email');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkResend = async () => {
    if (bulkSelection?.length === 0) return;
    if (!confirm(`Resend verification emails to ${bulkSelection?.length} users?`)) return;

    try {
      setLoading(true);
      setError('');
      const results = await accountVerificationService?.bulkResendEmails(bulkSelection);
      const successCount = results?.filter(r => r?.success)?.length;
      alert(`Successfully resent ${successCount} of ${bulkSelection?.length} emails`);
      setBulkSelection([]);
      await loadFailedConfirmations();
      onActionComplete?.();
    } catch (err) {
      setError(err?.message || 'Bulk resend failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkSelection = (userId) => {
    setBulkSelection(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredConfirmations = failedConfirmations?.filter(conf =>
    conf?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    conf?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const getFailureReasonBadge = (reason, isExpired) => {
    if (isExpired) {
      return <span className="px-2 py-1 text-xs font-bold rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">EXPIRED</span>;
    }
    if (reason?.includes('invalid')) {
      return <span className="px-2 py-1 text-xs font-bold rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">INVALID EMAIL</span>;
    }
    if (reason?.includes('server')) {
      return <span className="px-2 py-1 text-xs font-bold rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">SERVER ERROR</span>;
    }
    return <span className="px-2 py-1 text-xs font-bold rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">PENDING</span>;
  };

  if (loading && failedConfirmations?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Bulk Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            icon="Search"
          />
        </div>
        {bulkSelection?.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {bulkSelection?.length} selected
            </span>
            <Button size="sm" onClick={handleBulkResend}>
              <Icon name="Mail" size={14} className="mr-1" />
              Bulk Resend
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkSelection([])}>
              Clear
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={bulkSelection?.length === filteredConfirmations?.length && filteredConfirmations?.length > 0}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setBulkSelection(filteredConfirmations?.map(c => c?.userId));
                      } else {
                        setBulkSelection([]);
                      }
                    }}
                    className="rounded border-border"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Registration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Failure Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Retry Count</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredConfirmations?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-muted-foreground">
                    <Icon name="CheckCircle" className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No failed confirmations found</p>
                  </td>
                </tr>
              ) : (
                filteredConfirmations?.map((confirmation) => (
                  <tr key={confirmation?.userId} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={bulkSelection?.includes(confirmation?.userId)}
                        onChange={() => toggleBulkSelection(confirmation?.userId)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{confirmation?.fullName}</p>
                        <p className="text-xs text-muted-foreground">{confirmation?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">
                        {new Date(confirmation?.registrationDate)?.toLocaleDateString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(confirmation?.registrationDate)?.toLocaleTimeString()}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {getFailureReasonBadge(confirmation?.failureReason, confirmation?.isExpired)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{confirmation?.retryCount}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        onClick={() => handleResendEmail(confirmation?.userId, confirmation?.email)}
                        disabled={processingId === confirmation?.userId}
                      >
                        {processingId === confirmation?.userId ? (
                          <Icon name="Loader2" size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Icon name="Mail" size={14} className="mr-1" />
                            Resend
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <p>Showing {filteredConfirmations?.length} failed confirmation{filteredConfirmations?.length !== 1 ? 's' : ''}</p>
        <p>Last updated: {new Date()?.toLocaleTimeString()}</p>
      </div>
    </div>
  );
};

export default FailedConfirmationsTable;
